import { getAuth } from 'firebase/auth';

export type WorkflowSessionStatus = 'active' | 'idle' | 'expired' | 'completed' | 'error';

export interface WorkflowSession {
  sessionId: string;
  uid: string;
  tenantId?: string;
  title: string;
  status: WorkflowSessionStatus;
  workflowType?: string;
  messageCount: number;
  createdAt?: any;
  updatedAt?: any;
  lastMessageAt?: any;
  expiresAt?: any;
  metadata?: Record<string, unknown>;
}

export interface WorkflowSessionMeta {
  activeCount: number;
  /** null = unlimited (no organization carries a concurrent-session cap). */
  maxConcurrent: number | null;
  idleTtlMinutes: number;
}

function getApiBase(): string {
  if (typeof process.env.REACT_APP_API_BASEURL === 'string' && process.env.REACT_APP_API_BASEURL.trim()) {
    return process.env.REACT_APP_API_BASEURL.trim().replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return 'http://127.0.0.1:3001';
}

function getFunctionBase(): string {
  const apiBase = getApiBase();
  try {
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      const prodHostnames = new Set([
        'www.projectolivia.tech',
        'projectolivia.tech',
        'www.prest0.ai',
        'prest0.ai',
        'projectolivia.web.app',
        'projectolivia.firebaseapp.com',
      ]);
      if (prodHostnames.has(host)) {
        return 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';
      }
    }
  } catch {}
  return apiBase;
}

async function authHeaders(): Promise<Record<string, string>> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('AUTH_REQUIRED');
  const token = await user.getIdToken(false);
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function listWorkflowSessions(status?: WorkflowSessionStatus[], limit = 50): Promise<{ sessions: WorkflowSession[]; meta: WorkflowSessionMeta }> {
  const headers = await authHeaders();
  const base = getFunctionBase();
  const qs = new URLSearchParams();
  if (status?.length) qs.set('status', status.join(','));
  if (limit) qs.set('limit', String(limit));
  const res = await fetch(`${base}/listWorkflowSessions?${qs.toString()}`, { headers });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `HTTP_${res.status}`);
  return data;
}

export async function createWorkflowSession(input?: { title?: string; workflowType?: string; tenantId?: string }): Promise<WorkflowSession> {
  // PRE-70 fix: generate a local session ID immediately so localStorage isolation
  // works even if the backend call fails or is slow.
  const localId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  // Optimistic return — gives the UI something to work with right away
  const optimistic: WorkflowSession = {
    sessionId: localId,
    uid: getAuth().currentUser?.uid || '',
    title: input?.title || 'New Thread',
    status: 'active',
    workflowType: input?.workflowType || 'chat',
    messageCount: 0,
  };

  // Sync to backend in background; if it succeeds, update stored ID
  try {
    const headers = await authHeaders();
    const base = getFunctionBase();
    const res = await fetch(`${base}/createWorkflowSession`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...input, localSessionId: localId }),
    });
    const data = await res.json().catch(() => null);
    if (res.ok && data?.session?.sessionId) {
      // Migrate localStorage chat key from localId → server-assigned ID if different.
      // NOTE: We do NOT update 'prest0_active_workflow_session_id' here — the React component
      // is responsible for session ID state. Only migrate the chat history key.
      if (data.session.sessionId !== localId) {
        try {
          const existing = localStorage.getItem(`prest0_chat_session_${localId}`);
          if (existing) {
            localStorage.setItem(`prest0_chat_session_${data.session.sessionId}`, existing);
            localStorage.removeItem(`prest0_chat_session_${localId}`);
          }
        } catch { /* ignore */ }
      }
      return data.session;
    }
  } catch {
    // Backend unavailable — continue with local session (will sync on next touchSession)
  }

  return optimistic;
}

export async function completeWorkflowSession(sessionId: string): Promise<void> {
  const headers = await authHeaders();
  const base = getFunctionBase();
  const res = await fetch(`${base}/completeWorkflowSession`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ sessionId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `HTTP_${res.status}`);
  }
}

export async function renameWorkflowSession(sessionId: string, title: string): Promise<void> {
  const headers = await authHeaders();
  const base = getFunctionBase();
  const res = await fetch(`${base}/renameWorkflowSession`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ sessionId, title }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `HTTP_${res.status}`);
  }
}

export async function getWorkflowSessionLimits(): Promise<{
  /** null = unlimited (no organization carries a concurrent-session cap). */
  maxConcurrentSessions: number | null;
  sessionIdleTtlMinutes: number;
  sessionAbsoluteTtlHours: number;
  activeSessionCount: number;
  /** null when there is no cap to subtract from. */
  remainingSlots: number | null;
}> {
  const headers = await authHeaders();
  const base = getFunctionBase();
  const res = await fetch(`${base}/getWorkflowSessionLimits`, { headers });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `HTTP_${res.status}`);
  return data;
}

const SESSION_STORAGE_KEY = 'prest0_active_workflow_session_id';

export function getStoredWorkflowSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredWorkflowSessionId(sessionId: string | null): void {
  try {
    if (!sessionId) localStorage.removeItem(SESSION_STORAGE_KEY);
    else localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    // ignore storage failures
  }
}
